import { useGetList } from "react-admin";
import { FieldValues, Path } from "react-hook-form";
import { Student } from "../providers/studentsProvider";
import { StartingPoint } from "../providers/startingPointsProvider";
import { SelectInput } from "./SelectInput";
import { renderAddress } from "../misc/AddressDisplay";
import { DrivingLessonType } from "../model/DrivingLessonType";
import { useWatch } from "react-hook-form";
import { ReactNode, useMemo } from "react";
import { drivingSchoolsProvider } from "../providers/drivingSchoolsProvider";
import { assertDefined } from "../backoffice.utils";

export function StartingPointInput(props: {
  source: Path<FieldValues>;
  bulkEdit?: boolean;
  drivingLessonType?: DrivingLessonType;
  instructorId?: string;
  student: Student;
  size?: "small";
  fallbackStartingPoint?: false | StartingPoint;
}) {
  const { source, bulkEdit, instructorId, student, size, fallbackStartingPoint } = props;
  let drivingLessonType = useWatch<{ drivingLessonType?: "__DO_NOT_CHANGE__" | DrivingLessonType }>({
    name: "drivingLessonType",
  });
  if (!drivingLessonType || drivingLessonType === "__DO_NOT_CHANGE__") {
    drivingLessonType = props.drivingLessonType;
  }
  if (!drivingLessonType && bulkEdit) {
    drivingLessonType = "normal";
  }
  const { data: startingPoints = [] } = useGetList<StartingPoint>(
    "startingPoints",
    {
      sort: { field: "__apply_special_starting_point_sort_logic__", order: "ASC" },
      pagination: { page: 1, perPage: 999 },
      meta: { drivingLessonType, instructorId, student },
    },
    { enabled: !!drivingLessonType },
  );
  const label = drivingLessonType === "theoretischePruefung" ? "Ort" : "Startpunkt";
  const { groups, optionsWithGroups } = useMemo(() => {
    const groups = [
      "Gespeicherter Startpunkt",
      "TÜV/DEKRA",
      "Fahrschule",
      "Zugewiesene Filiale",
      "Weitere Filialen",
      "Weitere Startpunkte",
    ];
    const optionsWithGroups: Array<[string, ReactNode, string]> = [];
    if (fallbackStartingPoint) {
      optionsWithGroups.push([
        fallbackStartingPoint.id,
        renderAddress(fallbackStartingPoint.postalAddress, { oneLine: true }),
        "Gespeicherter Startpunkt",
      ]);
    }
    const drivingSchool = assertDefined(
      drivingSchoolsProvider.getOneFromCache(student.drivingSchoolId),
      `drivingSchoolsProvider.getOneFromCache(${JSON.stringify(student.drivingSchoolId)})`,
    );
    for (const startingPoint of startingPoints) {
      // See startingPointsProvider ...
      const group = startingPoint.id.startsWith("exam location ")
        ? "TÜV/DEKRA"
        : startingPoint.id.startsWith("branch ")
          ? (() => {
              if (drivingSchool.branches.length === 1) {
                return "Fahrschule";
              } else if (startingPoint.id === `branch ${student.branchId ?? drivingSchool.defaultBranch.uid}`) {
                return "Zugewiesene Filiale";
              } else {
                return "Weitere Filialen";
              }
            })()
          : "Weitere Startpunkte";
      if (drivingLessonType !== "theoretischePruefung" || group === "TÜV/DEKRA") {
        optionsWithGroups.push([
          startingPoint.id,
          `${startingPoint.name} (${renderAddress(startingPoint.postalAddress, { oneLine: true })})`,
          group,
        ]);
      }
    }
    return { groups, optionsWithGroups };
  }, [startingPoints]);
  return (
    <SelectInput
      source={source}
      bulkEdit={bulkEdit}
      label={label}
      groups={groups}
      optionsWithGroups={optionsWithGroups}
      validate={(value) => (value ? true : `Bitte wähle den ${label} aus.`)}
      size={size}
    />
  );
}
